/**
 * CapacityHistoryChart.tsx
 * Feature: 154 - Eventstream Real-Time Capacity Telemetry
 * Author: Peter Dobler
 * Date: 2026-05-18
 * Constitution Version: 1.12.0
 *
 * Recharts line chart for 30-second CU and throttling snapshots over time.
 * Spike-filtered points are excluded from rendering.
 */

import React, { useState } from 'react';
import { Button, Spinner, chartTooltipStyle } from '../ui';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { CapacityHistoryResponse } from '@shared/types/capacityTelemetry';
import { formatEasternTime } from '@/utils/dateFormatter';

interface CapacityHistoryChartProps {
  history: CapacityHistoryResponse | undefined;
  loading: boolean;
  windowMinutes: number;
  onWindowChange: (minutes: number) => void;
}

const WINDOW_OPTIONS = [
  { label: '30 min', value: 30 },
  { label: '2 hr', value: 120 },
  { label: '24 hr', value: 1440 },
];

function formatTime(isoString: string, windowMinutes: number): string {
  const d = new Date(isoString);
  if (windowMinutes <= 120) {
    return formatEasternTime(d);
  }
  return formatEasternTime(d);
}

export default function CapacityHistoryChart({
  history,
  loading,
  windowMinutes,
  onWindowChange,
}: CapacityHistoryChartProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 300 }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!history || history.snapshots.length === 0) {
    const isPaused = history?.stateEvents?.some((e) => e.eventType === 'paused');
    return (
      <div
        className="flex items-center justify-center text-[var(--color-text-secondary)]"
        style={{ height: 300 }}
      >
        <p className="text-sm">
          {isPaused ? 'No data — capacity is paused' : 'No telemetry data in selected window'}
        </p>
      </div>
    );
  }

  // Filter spike-flagged points before rendering
  const chartData = history.snapshots
    .filter((s) => !s.isSpikeFiltered)
    .map((s) => ({
      time: formatTime(s.windowStart, windowMinutes),
      cuPct: s.cuUtilizationPct !== null ? Math.round(s.cuUtilizationPct * 10) / 10 : null,
      delayPct:
        s.interactiveDelayThrottlingPct !== null
          ? Math.round(s.interactiveDelayThrottlingPct * 10) / 10
          : null,
    }));

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--color-text-secondary)]">
          CU Utilization &amp; Throttling History
        </span>
        <div className="inline-flex gap-1">
          {WINDOW_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              size="sm"
              variant={windowMinutes === opt.value ? 'primary' : 'outline'}
              onClick={() => onWindowChange(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 11 }}
            interval="preserveStartEnd"
          />
          <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} width={44} />
          <Tooltip
            {...chartTooltipStyle}
            formatter={(value, name) =>
              [`${typeof value === 'number' ? value : '—'}%`, name as string] as [string, string]
            }
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine y={80} stroke="#e53935" strokeDasharray="4 4" label={{ value: 'Throttle 80%', fontSize: 11, fill: '#e53935', position: 'right' }} />
          <Line
            type="monotone"
            dataKey="cuPct"
            name="CU Utilization"
            stroke="#1976d2"
            dot={false}
            strokeWidth={2}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="delayPct"
            name="Interactive Delay %"
            stroke="#f57c00"
            dot={false}
            strokeWidth={1.5}
            strokeDasharray="4 2"
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
